import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Check } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignIn() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const rules = [
    { ok: password.length >= 8, label: "At least 8 characters" },
    { ok: /[A-Z]/.test(password), label: "One uppercase letter" },
    { ok: /[0-9]/.test(password), label: "One number" },
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      nav("/auth-success", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Get started"
      title="Hire your AI Operations Executive"
      subtitle="Create your VELQUE account in seconds. Velque will be ready when you are."
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="lux-label">Your name</label>
          <input
            data-testid="signup-name-input"
            className="lux-input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Priya Sharma"
          />
        </div>
        <div>
          <label className="lux-label">Work email</label>
          <input
            data-testid="signup-email-input"
            className="lux-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="priya@company.com"
          />
        </div>
        <div>
          <label className="lux-label">Password</label>
          <div className="relative">
            <input
              data-testid="signup-password-input"
              className="lux-input pr-11"
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a strong password"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-nexa-mute hover:text-nexa-ink"
              aria-label="Toggle password"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {rules.map((r, i) => (
              <li key={i} className={`flex items-center gap-1.5 text-[12px] ${r.ok ? "text-[#22C55E]" : "text-nexa-mute"}`}>
                <Check className={`w-3.5 h-3.5 ${r.ok ? "opacity-100" : "opacity-40"}`} strokeWidth={2.5} />
                {r.label}
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <div data-testid="signup-error" className="text-[15px] text-[#EF4444] bg-[#EF4444]/8 border border-[#EF4444]/20 rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        <button data-testid="signup-submit-btn" className="lux-btn-primary w-full flex items-center justify-center" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </button>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center"><div className="w-full h-px bg-nexa-line" /></div>
          <div className="relative flex justify-center"><span className="bg-white px-3 text-base text-nexa-mute uppercase tracking-widest">or</span></div>
        </div>

        <button
          type="button"
          data-testid="signup-google-btn"
          onClick={googleSignIn}
          className="lux-btn-secondary w-full flex items-center justify-center gap-3"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.9 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 10-1.9 13.6-5.1l-6.3-5.2C29.3 34.9 26.8 36 24 36c-5.4 0-9.9-3.1-11.7-7.6l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.3 5.2C41 34.9 44 29.9 44 24c0-1.2-.1-2.3-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-center text-[15px] text-nexa-mute pt-2">
          Already have an account?{" "}
          <Link data-testid="signup-login-link" to="/login" className="lux-link">Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
